import { useEffect, useRef, useState } from "react";

import { useOverlay } from "@/Common/CustomHooks/useOverlay";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { PopulationBasedFundingFormData, PopulationBasedFundingFormEnum } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { PopulationBasedFundingEventDeregisteredReason } from "@/Common/Models/UI/Enums";
import { PopulationBasedFundingActions } from "@/Common/Services/PopulationBasedFunding/PopulationBasedFundingService";
import { ButtonControl } from "../Buttons/ButtonControl";
import { MultipleComponentPopup } from "../Modals/MultipleComponentPopup";
import { PopulationBasedFundingBasePopupBody } from "./PopulationBasedFundingBasePopupBody";

import "./PopulationBasedFundingPopUp.css";

interface IPopulationBasedFundingDeregistrationPopupProps {
    onClose: () => void;
    patientModel: Patient;
    currentStarredPhysician?: Person;
    onTrySubmit: (formData: PopulationBasedFundingFormData) => Promise<boolean>;
}

function PopulationBasedFundingDeregistrationPopup(props: IPopulationBasedFundingDeregistrationPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const [formData, setFormData] = useState<PopulationBasedFundingFormData>(() => new PopulationBasedFundingFormData({
        SelectedPhysician: props.currentStarredPhysician,
        SelectedEffectiveDate: new Date(),
        Notes: "",
        WillContinueToSeePhysician: false
    }));

    const [hasSaveButtonBeenClicked, setHasSaveButtonBeenClicked] = useState<boolean>(false);
    const [latestRegistrationEffectiveDate, setLatestRegistrationEffectiveDate] = useState<Date>(null);

    const isServerCallAlreadySent = useRef(false);

    useOverlay("populationBasedFunding-deregister-popup-overlay");

    useEffect(() => {
        const loadLatestRegistration = async (): Promise<void> => {
            const latestRegistration = await PopulationBasedFundingActions.getLatestRegistration(props.patientModel.InactivableResponse.Id);
            setLatestRegistrationEffectiveDate(latestRegistration.EffectiveDate);
        };

        loadLatestRegistration();
    }, [props.patientModel.InactivableResponse.Id]);

    const onSaveButtonClicked = async (): Promise<void> => {
        const onSave = async (): Promise<void> => {
            if (await props.onTrySubmit(formData)) {
                props.onClose();
            }
            else {
                isServerCallAlreadySent.current = false;
                setHasSaveButtonBeenClicked(false);
            }
        };

        if (!isServerCallAlreadySent.current) {
            isServerCallAlreadySent.current = true;
            await onSave();
        }

        setHasSaveButtonBeenClicked(true);
    };

    const isFormValid = (
        !IsNullOrUndefined(formData.SelectedDeregisterReason)
        && formData.SelectedEffectiveDate
        && formData.SelectedPhysician
        && (formData.SelectedDeregisterReason != PopulationBasedFundingEventDeregisteredReason.AnotherReasonIndicatedInNotes || !!formData.DeregistrationOrProposalCustomReason)
    );

    const renderFooter = (): JSX.Element => (
        <div className="p-footer">
            <div className="footer-button-container">
                <ButtonControl
                    className="btn btn-default"
                    onClick={() => props.onClose()}
                    label={getText("CancelLabel")}
                />
                <ButtonControl
                    id="save_registration_butt"
                    variant="brand"
                    label={getText("SaveLabel")}
                    onClick={onSaveButtonClicked}
                    disabled={hasSaveButtonBeenClicked || !isFormValid}
                />
            </div>
        </div>
    );

    return (
        <MultipleComponentPopup
            contentJustification="center"
            contentMarginTop={-30}
            onCloseCallback={props.onClose}
        >
            <div className="populationBasedFunding-popup popup">
                <div className="p-header">
                    <a className="close" aria-label="Close" onClick={() => props.onClose()}> x</a>
                    <h4>{getText("PopulationBasedFunding_DeregisterPatientLabel")}</h4>
                </div>
                <PopulationBasedFundingBasePopupBody
                    formType={PopulationBasedFundingFormEnum.Deregister}
                    formData={formData}
                    onChange={setFormData}
                    latestRegistrationEventEffectiveDate={latestRegistrationEffectiveDate}
                />
                {renderFooter()}
            </div>
        </MultipleComponentPopup>
    );
}

export { PopulationBasedFundingDeregistrationPopup };
